// Implements: F-PRV-008, F-PRV-006, F-PRV-011, F-PRV-012, F-PRV-013, F-PRV-007, F-PRV-009
/**
 * The controls of the viewer (the redesign mockup's `.pv-bar`, UX-05). Presentation only: every
 * change goes up through a callback.
 *
 * In order: the content tabs (Vista previa, Código, Consola), the share control the page hands in,
 * the device as a segmented control of icon buttons (and the free width when it is chosen), the
 * zoom, then reload, open in a new tab, download and full screen as icon buttons with their names.
 */

import type { ReactElement, ReactNode, RefObject } from "react";

import { useTranslation } from "../../../shared/i18n";
import { Button } from "../../../shared/ui/atoms/Button";
import { Icon } from "../../../shared/ui/atoms/Icon";
import { SegmentedControl } from "../../../shared/ui/molecules/SegmentedControl";
import { Tabs } from "../../../shared/ui/molecules/Tabs";
import { ZOOM_MAX, ZOOM_MIN } from "../model/viewport";
import type { ViewportMode, ViewportState } from "../model/viewport";
import styles from "./PreviewToolbar.module.css";

export type PreviewTab = "preview" | "code" | "console";

/** The zoom steps offered, as fractions; a stored value outside them is added so it stays shown. */
const ZOOM_STEPS = [0.5, 0.75, 1, 1.25, 1.5, 2] as const;

export interface PreviewToolbarProps {
  /** Prefix of the tab and panel ids (one viewer per page). */
  readonly idBase: string;
  readonly tab: PreviewTab;
  readonly onTab: (tab: PreviewTab) => void;
  /** The share control, right after the tabs. */
  readonly share?: ReactNode;
  readonly viewport: ViewportState;
  readonly canReload: boolean;
  readonly fullscreen: boolean;
  readonly fullscreenRef: RefObject<HTMLButtonElement | null>;
  readonly onMode: (mode: ViewportMode) => void;
  readonly onFreeWidth: (width: number) => void;
  readonly onZoom: (zoom: number) => void;
  readonly onReload: () => void;
  readonly onToggleFullscreen: () => void;
  /** The shown version's own document, opened in a new tab; absent when nothing is shown. */
  readonly openHref?: string | undefined;
  /** The attachment route of the shown version; absent when nothing is shown. */
  readonly downloadHref?: string | undefined;
  /** How many errors and warnings the frame relayed; shown on the Consola tab when above zero. */
  readonly consoleCount?: number | undefined;
}

function percent(zoom: number): string {
  return `${String(Math.round(zoom * 100))} %`;
}

export function PreviewToolbar({
  idBase,
  tab,
  onTab,
  share,
  viewport,
  canReload,
  fullscreen,
  fullscreenRef,
  onMode,
  onFreeWidth,
  onZoom,
  onReload,
  onToggleFullscreen,
  openHref,
  downloadHref,
  consoleCount,
}: PreviewToolbarProps): ReactElement {
  const { t } = useTranslation();
  const steps: readonly number[] = (ZOOM_STEPS as readonly number[]).includes(viewport.zoom)
    ? ZOOM_STEPS
    : [...ZOOM_STEPS, viewport.zoom].sort((left, right) => left - right);
  return (
    <div className={styles.toolbar} role="toolbar" aria-label={t("preview.toolbar.label")}>
      <Tabs
        label={t("preview.tabs.label")}
        idBase={idBase}
        items={[
          { id: "preview", label: t("preview.tabs.preview"), icon: "eye" },
          { id: "code", label: t("preview.tabs.code"), icon: "code" },
          {
            id: "console",
            label: t("preview.tabs.console"),
            icon: "terminal",
            title: t("preview.console.tabHint"),
            count: consoleCount !== undefined && consoleCount > 0 ? consoleCount : undefined,
          },
        ]}
        selected={tab}
        onSelect={(id) => {
          onTab(id === "code" ? "code" : id === "console" ? "console" : "preview");
        }}
      />
      {share}
      <span className={styles.separator} aria-hidden="true" />
      <SegmentedControl<ViewportMode>
        label={t("preview.viewport.group")}
        iconOnly
        options={[
          { value: "desktop", label: t("preview.viewport.desktop"), icon: "monitor" },
          { value: "tablet", label: t("preview.viewport.tablet"), icon: "tablet" },
          { value: "mobile", label: t("preview.viewport.mobile"), icon: "phone" },
          { value: "free", label: t("preview.viewport.free"), icon: "free" },
        ]}
        value={viewport.mode}
        onChange={onMode}
      />
      {viewport.mode === "free" ? (
        <label className={styles.free}>
          <span className={styles.srOnly}>{t("preview.viewport.freeWidth")}</span>
          <input
            className={styles.number}
            type="number"
            inputMode="numeric"
            min={0}
            value={viewport.freeWidth}
            onChange={(event) => {
              const next = event.target.valueAsNumber;
              if (Number.isFinite(next)) onFreeWidth(next);
            }}
          />
        </label>
      ) : null}
      <select
        className={styles.zoom}
        aria-label={t("preview.zoom.label")}
        value={String(viewport.zoom)}
        onChange={(event) => {
          const next = Number(event.target.value);
          if (Number.isFinite(next) && next >= ZOOM_MIN && next <= ZOOM_MAX) onZoom(next);
        }}
      >
        {steps.map((step) => (
          <option key={step} value={String(step)}>
            {percent(step)}
          </option>
        ))}
      </select>
      <span className={styles.spacer} />
      <Button variant="ghost" iconOnly label={t("preview.reload")} disabled={!canReload} onClick={onReload}>
        <Icon name="refresh" />
      </Button>
      {openHref === undefined ? null : (
        <a
          className={styles.iconLink}
          href={openHref}
          target="_blank"
          rel="noopener noreferrer"
          aria-label={t("preview.openTab")}
        >
          <Icon name="external" />
        </a>
      )}
      {downloadHref === undefined ? null : (
        <a className={styles.iconLink} href={downloadHref} download aria-label={t("preview.download")}>
          <Icon name="download" />
        </a>
      )}
      <Button
        variant="ghost"
        iconOnly
        buttonRef={fullscreenRef}
        label={fullscreen ? t("preview.fullscreen.exit") : t("preview.fullscreen.enter")}
        onClick={onToggleFullscreen}
      >
        <Icon name="maximize" />
      </Button>
    </div>
  );
}
